import IconButton from '@material-ui/core/IconButton';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CloseIcon from '@material-ui/icons/Close';
import { useEffect } from 'react';
import { NotificationBanner } from '../../../../shared';
import RedesignModal from '../../../../shared/components/RedesignModal';
import { formatTestDuration } from '../../../../shared/utils/formatTestDuration';
import { redesignTheme } from '../../../../theme';
import { usePendingTestsModalStyles } from './PendingTestsModal.styles';

const { spacing } = redesignTheme;

// Extra margin beyond safe-area so Safari chrome never covers the modal
const overlaySafeAreaStyle = {
  paddingTop: `calc(${spacing.md}px + env(safe-area-inset-top, 0px))`,
  paddingRight: `calc(${spacing.md}px + env(safe-area-inset-right, 0px))`,
  paddingBottom: `calc(${spacing.lg}px + env(safe-area-inset-bottom, 0px))`,
  paddingLeft: `calc(${spacing.md}px + env(safe-area-inset-left, 0px))`,
} as const;

export type PendingTest = {
  id: number | string;
  title: string;
  time?: string;
};

export type PendingTestsModalProps = {
  open: boolean;
  tests: PendingTest[];
  onClose: () => void;
  onTestClick: (test: PendingTest) => void;
  showBanner?: boolean;
  onOpen?: () => void;
};

const PendingTestsModal = ({
  open,
  tests,
  onClose,
  onTestClick,
  showBanner = false,
  onOpen,
}: PendingTestsModalProps) => {
  const classes = usePendingTestsModalStyles();

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const { body } = document;
    const { scrollY } = window;
    const previous = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';

    return () => {
      body.style.overflow = previous.overflow;
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.width = previous.width;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  return (
    <>
      {showBanner && tests.length > 0 && (
        <NotificationBanner
          label="Vos tests en attente"
          count={tests.length}
          actionLabel="Voir mes tests"
          onClick={onOpen}
        />
      )}

      <RedesignModal
        open={open}
        onClose={onClose}
        labelledBy="pending-tests-modal-title"
        paperClassName={classes.modal}
        zIndex={1304}
        usePortal={true}
        overlayClassName={classes.overlayScroll}
        overlayStyle={overlaySafeAreaStyle}>
        <div className={classes.headerBlock}>
          <div className={classes.headerRow}>
            <h2 id="pending-tests-modal-title" className={classes.title}>
              Votre consultant(e) vous invite à passer les tests suivants
            </h2>
            <IconButton className={classes.closeButton} onClick={onClose} aria-label="Fermer">
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </div>
          <p className={classes.subtitle}>Cliquez sur un test pour y accéder</p>
        </div>

        <div className={classes.list}>
          {tests.map(test => (
            <button
              key={test.id}
              type="button"
              className={classes.item}
              onClick={() => onTestClick(test)}>
              <span className={classes.itemTitle}>{test.title}</span>
              <span className={classes.timeRow}>
                <AccessTimeIcon className={classes.timeIcon} aria-hidden="true" />
                {formatTestDuration(test.time)}
              </span>
            </button>
          ))}
        </div>
      </RedesignModal>
    </>
  );
};

export default PendingTestsModal;
